/**
 * The text player (ADR-116; handoff §10–§14, §19.8–§19.9): reading, then filling with
 * the answer field and the word bank, per-gap feedback with the hint ladder, leaving
 * and resuming, and completion with the text level. One state machine, two layouts:
 * below 768 px the phone's full-screen frame; from 768 px the desktop's body inside
 * the app shell (sidebar and the screen-header bar carry back and identity), a centred
 * 760 px column, driven fully from a physical keyboard (Enter, ↑/↓, Esc).
 *
 * Every verdict is the server's (Decision 3): the client never compares an answer.
 * Practice writes no learning state, and nothing here shows a mastery indicator.
 *
 * The keyboard stays up through filling (ADR-011, ADR-013, ADR-014 §6): the answer
 * field stays mounted and focused; moving between gaps, the 650 ms advance and the
 * window's scroll only change state; taps on the text, the bank and the arrows never
 * take focus. Only the leave sheet and the summary let it go, on purpose.
 *
 * The text is not selectable here (Stanley's decision): the whole frame is a control
 * surface, so a missed tap opens no Touch to Search and no OS selection.
 *
 * A shared link's reader runs the same player in viewer mode (handoff §17.2): the actions
 * are `useSharedPractice`'s, the reading screen is the shared page's own, leaving goes
 * back to it (and says the run is not kept), and completion has no level to report.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowDown, ArrowUp, Check, X } from 'lucide-react'
import { useLocale } from '../i18n/context'
import { useHistorySentinel } from '../hooks/useHistorySentinel'
import { useIsDesktop } from '../hooks/useIsDesktop'
import { useScreenHeader } from '../hooks/useScreenHeader'
import { usePracticeActions, usePracticeView, type PracticeActions } from '../hooks/useTextPractice'
import type { ScreenHeaderConfig } from '../stores/screenHeaderStore'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../utils/controlSurface'
import type { PracticeCompletion, PracticeGap, PracticeVerdict, PracticeView } from '../types'
import AnswerField, { type AnswerFieldHandle, type AnswerTone } from '../components/texts/player/AnswerField'
import FillColumn from '../components/texts/player/FillColumn'
import PlayerSheet from '../components/texts/player/PlayerSheet'
import PlayerText from '../components/texts/player/PlayerText'
import { Eyebrow, GhostButton, OutlinePill, PrimaryButton, SecondaryPill } from '../components/texts/ui'

const ADVANCE_MS = 650
const AUTO_SCROLL_MS = 600

interface RouteState {
  from?: string
  fromLabel?: string
}

type Phase = 'reading' | 'filling' | 'completion'

/** The message slot's verdict line for the active gap, cleared when the gap changes. */
type Flash =
  | { kind: 'correct' | 'error' }
  | { kind: 'fits' | 'wrong'; letter: string }
  | { kind: 'revealed'; answer: string }

type SystemLine = 'resumed' | 'bankOpened' | null

/** A shared link's reader (handoff §17.2): the page around the player supplies these. */
export interface ViewerMode {
  /** The shared page's reading screen, given the handler that starts filling in the same tap. */
  reading: (startFilling: () => void) => React.ReactNode
  /** The desktop bar while that screen shows. */
  readingHeader: ScreenHeaderConfig | null
  /** «Сохранить в мои тексты» on the completion. */
  onSave: () => void
}

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
const done = (g: PracticeGap | undefined) => !!g && g.state !== 'empty'

export default function TextPlayerPage() {
  const { textId } = useParams<{ textId: string }>()
  const { tr } = useLocale()
  const { data: view, isLoading, isError, error, refetch } = usePracticeView(textId)
  const actions = usePracticeActions(textId ?? '')
  if (isLoading) return <div aria-busy="true" style={{ minHeight: 240 }} />
  if ((error as { response?: { status?: number } } | null)?.response?.status === 404) return <Navigate to="/library" replace />
  if (isError || !view || !textId) {
    return (
      <div role="alert" style={{ textAlign: 'center', padding: '64px 16px', fontFamily: 'var(--font-ui)' }}>
        <p style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{tr.textPlayer.loadFailed}</p>
        <SecondaryPill onClick={() => refetch()}>{tr.textPlayer.retry}</SecondaryPill>
      </div>
    )
  }
  if (view.gaps.length === 0) return <Navigate to={`/texts/${textId}`} replace />
  return <Player textId={textId} view={view} refetch={refetch} actions={actions} />
}

export function Player({ textId, view, refetch, actions, viewer }: {
  textId: string
  view: PracticeView
  refetch: () => Promise<unknown>
  actions: PracticeActions
  viewer?: ViewerMode
}) {
  const { tr } = useLocale()
  const t = tr.textPlayer
  const navigate = useNavigate()
  const location = useLocation()
  const route = (location.state as RouteState | null) ?? {}
  const isViewer = !!viewer
  // The layout follows the width; the input follows the device. A wide touch screen (a tablet,
  // a phone on its side) keeps the soft-keyboard field and its recovery rules (ADR-116
  // Implementation note 4).
  const desktop = useIsDesktop()
  const [touch] = useState(() => window.matchMedia?.('(pointer: coarse)').matches ?? false)
  const physicalKeyboard = desktop && !touch

  // A run in progress opens filling directly, at its gap (handoff §13).
  const [phase, setPhase] = useState<Phase>(() => (view.attempt ? 'filling' : 'reading'))
  const [system, setSystem] = useState<SystemLine>(() => (view.attempt ? 'resumed' : null))
  const [completion, setCompletion] = useState<PracticeCompletion | null>(null)
  const [leaving, setLeaving] = useState(false)

  const gaps = useMemo(() => new Map(view.gaps.map((g) => [g.id, g])), [view.gaps])
  const order = view.gaps.map((g) => g.id)
  const [activeId, setActiveId] = useState<string | null>(() => view.attempt?.active_gap_id ?? view.gaps[0]?.id ?? null)
  const active = activeId ? gaps.get(activeId) : undefined

  // What the learner typed per gap, kept when they move away and come back.
  const drafts = useRef(new Map<string, string>())
  const [value, setValue] = useState('')
  const [flash, setFlash] = useState<Flash | null>(null)
  const field = useRef<AnswerFieldHandle>(null)
  // «К пропуску»: the active gap has left the window after a free scroll.
  const [pill, setPill] = useState(false)
  const advanceTimer = useRef<number | null>(null)

  // A reader goes back to the shared page's reading screen, and the run is dropped (§17.2).
  const toReading = useRef<() => void>(() => {})
  const leaveTo = useCallback(() => {
    if (isViewer) toReading.current()
    else navigate(`/texts/${textId}`, { replace: true, state: { from: route.from, fromLabel: route.fromLabel } })
  }, [isViewer, navigate, textId, route.from, route.fromLabel])

  // ── Moving between gaps ─────────────────────────────────────────────────────
  const goTo = (id: string | null, persist = true) => {
    if (advanceTimer.current !== null) { window.clearTimeout(advanceTimer.current); advanceTimer.current = null }
    if (!id) return
    setActiveId(id)
    setFlash(null)
    setPill(false)
    setSystem((s) => (s === 'bankOpened' ? s : null))
    const gap = gaps.get(id)
    setValue(gap?.state === 'answered' ? gap.answer ?? '' : drafts.current.get(id) ?? '')
    if (persist && view.attempt) actions.setActive.mutate(id)
  }

  const nextUnanswered = (from: string | null, inGaps: Map<string, PracticeGap>): string | null => {
    const start = from ? order.indexOf(from) : -1
    for (let k = 1; k <= order.length; k++) {
      const id = order[(start + k) % order.length]
      if (!done(inGaps.get(id))) return id
    }
    return null
  }

  const remaining = view.attempt?.remaining ?? view.gaps.filter((g) => !done(g)).length
  const allDone = phase === 'filling' && remaining === 0

  // ── Answering ───────────────────────────────────────────────────────────────
  // Set in the same tick as the tap: a second tap before the next render must not send
  // again (the server would count it once, but this screen would lose the first verdict).
  const inFlight = useRef(false)
  const pending = actions.answer.isPending || actions.accept.isPending

  const gapsRef = useRef(gaps)
  useEffect(() => { gapsRef.current = gaps }, [gaps])

  const afterCorrect = (gapId: string) => {
    advanceTimer.current = window.setTimeout(() => {
      advanceTimer.current = null
      // The cache holds the newest gaps by now; the next unanswered one, wrapping.
      const next = nextUnanswered(gapId, gapsRef.current)
      // Nothing left: «Верно.» gives way to «Все пропуски заполнены.» and «Итоги».
      if (next) goTo(next)
      else setFlash(null)
    }, ADVANCE_MS)
  }

  const show = (verdict: PracticeVerdict, gap: PracticeGap) => {
    if (verdict === 'correct') { setFlash({ kind: 'correct' }); afterCorrect(gap.id) }
    else if (verdict === 'fits') setFlash({ kind: 'fits', letter: gap.first_letter ?? '' })
    else if (verdict === 'wrong') setFlash({ kind: 'wrong', letter: gap.first_letter ?? '' })
    else if (verdict === 'revealed') setFlash({ kind: 'revealed', answer: gap.answer ?? '' })
    if (verdict !== 'ignored') setSystem(null)
  }

  const submit = () => {
    if (!active || !view.attempt || pending || inFlight.current) return
    if (done(active)) {
      // ✓ / Enter on a done gap moves on (handoff §12).
      const next = nextUnanswered(active.id, gaps)
      if (next) goTo(next)
      // Keyboard only: with every gap done, Enter in the field is «Итоги» (handoff §12).
      else if (desktop && allDone) finish()
      return
    }
    // An empty submit is not an answer (ADR-116 Decision 2): nothing is sent or shown.
    if (!value.trim()) return
    inFlight.current = true
    actions.answer.mutate(
      { gapId: active.id, answer: value, expectedWrongCount: active.wrong_count },
      {
        onSuccess: (r) => show(r.verdict, r.gap),
        onError: () => setFlash({ kind: 'error' }),
        onSettled: () => { inFlight.current = false },
      },
    )
  }

  const acceptMine = () => {
    if (!active || !view.attempt || pending || inFlight.current || !value.trim()) return
    inFlight.current = true
    actions.accept.mutate(
      { gapId: active.id, answer: value },
      {
        onSuccess: (r) => show(r.verdict, r.gap),
        onError: () => setFlash({ kind: 'error' }),
        onSettled: () => { inFlight.current = false },
      },
    )
  }

  const onType = (next: string) => {
    setValue(next)
    if (activeId) drafts.current.set(activeId, next)
  }

  // ── The window ──────────────────────────────────────────────────────────────
  const windowRef = useRef<HTMLDivElement | null>(null)
  const autoScrolling = useRef(false)

  const activeEl = useCallback(() => (activeId
    ? windowRef.current?.querySelector<HTMLElement>(`[data-gap-first="${activeId}"]`) ?? null
    : null), [activeId])

  const scrollToActive = useCallback(() => {
    const box = windowRef.current
    const el = activeEl()
    if (!box || !el) return
    // The active gap's top at 30 % of the window's height (handoff §11.3).
    const top = el.offsetTop - box.clientHeight * 0.3
    autoScrolling.current = true
    box.scrollTo({ top: Math.max(0, top), behavior: reducedMotion() ? 'auto' : 'smooth' })
    window.setTimeout(() => { autoScrolling.current = false }, reducedMotion() ? 0 : AUTO_SCROLL_MS)
  }, [activeEl])

  useLayoutEffect(() => {
    if (phase === 'filling') scrollToActive()
  }, [phase, activeId, scrollToActive])

  const onWindowScroll = () => {
    if (autoScrolling.current) return
    const box = windowRef.current
    const el = activeEl()
    if (!box || !el) return
    const b = box.getBoundingClientRect()
    const r = el.getBoundingClientRect()
    setPill(r.bottom < b.top || r.top > b.bottom)
  }

  // ── Keyboard: up on entering filling, and kept up through it ────────────────
  const keepFocus = phase === 'filling' && !leaving
  useLayoutEffect(() => {
    if (phase === 'filling' && !leaving) field.current?.focus()
  }, [phase, leaving])

  const startFilling = () => {
    // Render filling and focus in this same tap, so Android raises the keyboard.
    flushSync(() => { setPhase('filling'); setSystem(null) })
    field.current?.focus()
    actions.start.mutate(undefined, {
      onSuccess: (fresh) => {
        const first = fresh.attempt?.active_gap_id ?? fresh.gaps[0]?.id ?? null
        setActiveId(first)
      },
    })
  }

  // ── Leaving ─────────────────────────────────────────────────────────────────
  const requestLeave = () => {
    if (phase !== 'filling' || !view.attempt || view.attempt.answered === 0) return leaveTo()
    field.current?.release()
    setLeaving(true)
  }
  useHistorySentinel(phase === 'filling' && !leaving, 'text-player', requestLeave)

  const stay = () => {
    setLeaving(false)
    field.current?.focus()
  }

  // ── The desktop bar and Esc ─────────────────────────────────────────────────
  // The bar's back link and Esc take the same road as the phone's ×: asks only when there
  // are answers. They read the latest handler through a ref, so the bar's config is stable.
  const leaveRef = useRef<() => void>(() => {})
  useEffect(() => { leaveRef.current = requestLeave })
  const barBack = useCallback(() => leaveRef.current(), [])
  const ts = tr.textShare
  const subtitle = phase === 'completion' ? (isViewer ? ts.deskSubtitleDone : t.deskSubtitleDone)
    : phase === 'filling' ? (isViewer ? ts.deskSubtitle(remaining) : t.deskSubtitle(view.level, remaining))
    : t.deskSubtitleReading(view.level)
  const readingHeader = viewer?.readingHeader ?? null
  const headerConfig = useMemo(
    () => (!desktop ? null
      : isViewer && phase === 'reading' ? readingHeader
      : { back: { label: t.backToText, onClick: phase === 'completion' ? leaveTo : barBack }, title: view.title, subtitle }),
    [desktop, isViewer, readingHeader, t.backToText, phase, leaveTo, barBack, view.title, subtitle],
  )
  useScreenHeader(headerConfig)
  useEffect(() => {
    // The shared page's reading screen is a page, not the player: Esc does not leave it.
    if (!desktop || leaving || phase === 'completion' || (isViewer && phase === 'reading')) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      e.preventDefault()
      leaveRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [desktop, leaving, phase, isViewer])

  // ── Completion ──────────────────────────────────────────────────────────────
  const finish = () => {
    field.current?.release()
    actions.complete.mutate(undefined, {
      onSuccess: (result) => { setCompletion(result); setPhase('completion') },
      onError: () => setFlash({ kind: 'error' }),
    })
  }
  // The reader's run is dropped whole: back to the page, nothing kept.
  const backToReading = () => {
    if (advanceTimer.current !== null) { window.clearTimeout(advanceTimer.current); advanceTimer.current = null }
    actions.restart.mutate()
    drafts.current.clear()
    setCompletion(null)
    setFlash(null)
    setValue('')
    setLeaving(false)
    setSystem(null)
    setPhase('reading')
  }
  useEffect(() => { toReading.current = backToReading })
  const again = () => {
    if (isViewer) return backToReading()
    actions.restart.mutate(undefined, {
      onSuccess: async () => {
        await refetch()
        drafts.current.clear()
        setCompletion(null)
        setFlash(null)
        setValue('')
        setPhase('reading')
      },
    })
  }

  useEffect(() => () => { if (advanceTimer.current !== null) window.clearTimeout(advanceTimer.current) }, [])

  const frame: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 40, display: 'flex', flexDirection: 'column',
    background: 'var(--bg-page)', fontFamily: 'var(--font-ui)', height: '100dvh',
  }
  const column: React.CSSProperties = { width: '100%', maxWidth: 560, margin: '0 auto', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }

  const strip = (right: React.ReactNode) => (
    <header style={{
      height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, padding: '0 16px 0 4px',
      background: 'var(--bg-chrome)', borderBottom: '1px solid var(--border-chrome)',
    }}>
      <button
        type="button"
        onClick={phase === 'filling' ? requestLeave : leaveTo}
        onPointerDown={(e) => e.preventDefault()}
        aria-label={t.close}
        data-testid="player-close"
        style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)' }}
      >
        <X size={22} />
      </button>
      {phase !== 'completion' && (
        <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {view.title}
        </span>
      )}
      {right}
    </header>
  )

  // ── Reading (§10) ───────────────────────────────────────────────────────────
  if (phase === 'reading' && viewer) return <ViewerReading render={viewer.reading} onStart={startFilling} />
  if (phase === 'reading') {
    const repeats = view.gaps.some((g) => g.places > 1)
    const levelLine = view.level === 1 ? t.readLevel1 : view.level === 2 ? t.readLevel2 : t.readLevel3
    const levelCard = (
      <section data-testid="player-level" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 16, border: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <span style={{ fontFamily: 'var(--font-content)', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)' }}>{view.level}</span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Eyebrow>{t.levelEyebrow(view.max_level)}</Eyebrow>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{levelLine}</span>
        </span>
      </section>
    )
    const countLine = `${t.readCount(view.gaps.length)}${repeats ? ` ${t.readRepeat}` : ''}`
    if (desktop) {
      // Every action lives in the body (§19.0): the bar stays back + identity. The primary is
      // focused, so Enter starts filling and Esc leaves.
      return (
        <div data-testid="player-reading" className={CONTROL_SURFACE} onContextMenu={suppressTouchContextMenu}
          style={{ width: '100%', maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 14, fontFamily: 'var(--font-ui)' }}>
          {levelCard}
          <div style={{ padding: '24px 32px', borderRadius: 16, border: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
            <PlayerText segments={view.segments} gaps={gaps} mode="reading" fontSize={19} lineHeight={38} />
          </div>
          <div style={{ position: 'sticky', bottom: 0, display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0', background: 'var(--bg-page)' }}>
            <p style={{ flex: 1, margin: 0, fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)' }} data-testid="player-read-count">{countLine}</p>
            <PrimaryButton height={48} onClick={startFilling} data-testid="player-fill" autoFocus>{t.fill}</PrimaryButton>
          </div>
        </div>
      )
    }
    return (
      <div data-testid="player-reading" className={CONTROL_SURFACE} onContextMenu={suppressTouchContextMenu} style={frame}>
        {strip(<span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t.reading}</span>)}
        <div style={{ ...column, overflowY: 'auto' }}>
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <section data-testid="player-level" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 16, border: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
              <span style={{ fontFamily: 'var(--font-content)', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)' }}>{view.level}</span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Eyebrow>{t.levelEyebrow(view.max_level)}</Eyebrow>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{levelLine}</span>
              </span>
            </section>
            <PlayerText segments={view.segments} gaps={gaps} mode="reading" fontSize={18} lineHeight={34} />
          </div>
        </div>
        <footer style={{ flexShrink: 0, background: 'var(--bg-chrome)', borderTop: '1px solid var(--border-chrome)', padding: '12px 16px calc(14px + env(safe-area-inset-bottom, 0px))' }}>
          <div style={{ ...column, gap: 10 }}>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)' }} data-testid="player-read-count">
              {t.readCount(view.gaps.length)}{repeats ? ` ${t.readRepeat}` : ''}
            </p>
            <PrimaryButton full height={52} onClick={startFilling} data-testid="player-fill">{t.fill}</PrimaryButton>
          </div>
        </footer>
      </div>
    )
  }

  // ── Completion (§14) ────────────────────────────────────────────────────────
  if (phase === 'completion' && completion) {
    if (desktop) {
      return (
        <div data-testid="player-completion" className={CONTROL_SURFACE} onContextMenu={suppressTouchContextMenu}
          style={{ width: '100%', maxWidth: 560, margin: '24px auto 0', boxSizing: 'border-box', padding: 28, borderRadius: 16, border: '1px solid var(--border)', background: 'var(--bg-surface)', fontFamily: 'var(--font-ui)' }}>
          <Completion result={completion} title={view.title} onAgain={again} onView={leaveTo} pendingAgain={actions.restart.isPending} desktop onDone={leaveTo} onSave={viewer?.onSave} />
        </div>
      )
    }
    return (
      <div data-testid="player-completion" className={CONTROL_SURFACE} onContextMenu={suppressTouchContextMenu} style={frame}>
        {strip(null)}
        <Completion result={completion} title={view.title} onAgain={again} onView={leaveTo} pendingAgain={actions.restart.isPending} onSave={viewer?.onSave} />
        <footer style={{ flexShrink: 0, background: 'var(--bg-chrome)', borderTop: '1px solid var(--border-chrome)', padding: '12px 16px calc(14px + env(safe-area-inset-bottom, 0px))' }}>
          <div style={column}>
            {viewer
              ? <PrimaryButton full height={52} onClick={viewer.onSave} data-testid="player-save">{ts.saveCta}</PrimaryButton>
              : <PrimaryButton full height={52} onClick={leaveTo} data-testid="player-done">{t.done}</PrimaryButton>}
          </div>
        </footer>
      </div>
    )
  }

  // ── Filling (§11, §12) ──────────────────────────────────────────────────────
  const bankState = view.bank_state
  const tone: AnswerTone =
    flash?.kind === 'correct' ? 'correct'
    : flash?.kind === 'wrong' || flash?.kind === 'revealed' || flash?.kind === 'error' ? 'wrong'
    : flash?.kind === 'fits' ? 'fits'
    : done(active) || allDone ? 'neutral'
    : 'idle'
  const offerMine = view.can_accept && !!flash && (flash.kind === 'wrong' || flash.kind === 'revealed') && value.trim() !== ''

  const fs = desktop ? 14 : 13
  const slot = (() => {
    const line = (text: string, opts: { muted?: boolean; check?: boolean; action?: React.ReactNode; testId?: string } = {}) => (
      <div data-testid={opts.testId ?? 'player-slot-line'} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        {opts.check && <Check size={18} aria-hidden style={{ color: 'var(--success)', flexShrink: 0 }} />}
        <span style={{
          flex: 1, minWidth: 0, fontSize: fs, lineHeight: desktop ? 1.4 : 1.35, color: opts.muted ? 'var(--text-secondary)' : 'var(--text-primary)',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>{text}</span>
        {opts.action}
      </div>
    )
    if (flash) {
      const mine = offerMine && (
        <OutlinePill onPointerDown={(e) => e.preventDefault()} onClick={acceptMine} data-testid="player-my-answer" style={{ flexShrink: 0 }}>
          {t.myAnswerFits}
        </OutlinePill>
      )
      if (flash.kind === 'correct') return line(t.correct, { check: true, testId: 'player-slot-correct' })
      if (flash.kind === 'fits') return line(t.fits(flash.letter), { testId: 'player-slot-fits' })
      if (flash.kind === 'wrong') return line(t.wrong(flash.letter), { action: mine, testId: 'player-slot-wrong' })
      if (flash.kind === 'revealed') return line(t.revealed(flash.answer), { action: mine, testId: 'player-slot-revealed' })
      return line(t.checkFailed, { testId: 'player-slot-error' })
    }
    if (allDone) {
      return line(t.allDone, {
        testId: 'player-slot-all-done',
        action: (
          <OutlinePill onPointerDown={(e) => e.preventDefault()} onClick={finish} disabled={actions.complete.isPending} data-testid="player-summary" style={{ flexShrink: 0 }}>
            {t.summary}
          </OutlinePill>
        ),
      })
    }
    if (system === 'bankOpened') return line(t.bankOpened, { muted: true, testId: 'player-slot-bank-opened' })
    if (system === 'resumed') return line(t.resumed, { testId: 'player-slot-resumed' })
    if (done(active)) return line(t.revisiting, { muted: true, testId: 'player-slot-revisiting' })
    if (active?.label) {
      return (
        <div data-testid="player-slot-label" style={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: desktop ? 10 : 8, minWidth: 0 }}>
            <Eyebrow style={{ flexShrink: 0 }}>{t.formEyebrow}</Eyebrow>
            <span style={{ fontSize: fs, lineHeight: desktop ? 1.4 : 1.35, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {tr.learn.formLabel(active.label)}
            </span>
          </div>
          {active.places > 1 && <span style={{ fontSize: 12, lineHeight: 1.3, color: 'var(--text-secondary)' }}>{t.repeatWithLabel(active.places)}</span>}
        </div>
      )
    }
    return line(active && active.places > 1 ? t.noLabelRepeat(active.places) : t.noLabel, { muted: true, testId: 'player-slot-hint' })
  })()

  const index = activeId ? order.indexOf(activeId) : -1
  const arrow = (dir: 'prev' | 'next') => {
    const target = dir === 'prev' ? order[index - 1] : order[index + 1]
    return (
      <button
        type="button"
        aria-label={dir === 'prev' ? t.prevGap : t.nextGap}
        data-testid={`player-${dir}`}
        disabled={!target}
        onPointerDown={(e) => e.preventDefault()}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => target && goTo(target)}
        style={{
          width: 44, height: 44, flexShrink: 0, borderRadius: '50%', border: '2px solid var(--border)', background: 'var(--bg-surface)',
          color: target ? 'var(--text-primary)' : 'var(--text-tertiary)', cursor: target ? 'pointer' : 'default',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
        }}
      >
        {dir === 'prev' ? <ArrowUp size={18} /> : <ArrowDown size={18} />}
      </button>
    )
  }

  const bankRow = bankState === 'none' ? null : (
    <div
      data-testid="player-bank"
      aria-label={t.bankLabel}
      onPointerDown={(e) => e.preventDefault()}
      style={desktop
        ? { flexShrink: 0, minHeight: 44, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }
        : { flexShrink: 0, minHeight: 58, display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', overflowX: 'auto' }}
    >
      {bankState === 'closed' ? (
        <>
          <OutlinePill
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => actions.openBank.mutate(undefined, { onSuccess: () => { setFlash(null); setSystem('bankOpened') } })}
            disabled={!view.attempt}
            data-testid="player-open-bank"
            style={{ flexShrink: 0 }}
          >
            {t.openBank}
          </OutlinePill>
          <span style={{ fontSize: 12, lineHeight: 1.35, color: 'var(--text-tertiary)' }}>{t.bankHint}</span>
        </>
      ) : (
        <>
          {desktop && view.level === 1 && <Eyebrow style={{ flexShrink: 0 }}>{t.bankLabel}</Eyebrow>}
          {view.level === 2 && <span style={{ flexShrink: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>{t.bankPrefix}</span>}
          {(view.bank ?? []).map((chip, i) => (
            <button
              key={`${chip.form}-${i}`}
              type="button"
              data-testid="player-chip"
              data-used={chip.used || undefined}
              disabled={chip.used}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onType(chip.form); if (desktop) field.current?.focus() }}
              style={{
                flexShrink: 0, height: 36, padding: '0 12px', borderRadius: 'var(--radius-pill)', cursor: chip.used ? 'default' : 'pointer',
                fontFamily: 'var(--font-content)', fontSize: 15,
                border: chip.used ? '1px solid transparent' : '1px solid var(--border)',
                background: chip.used ? 'transparent' : 'var(--bg-surface)',
                color: chip.used ? 'var(--text-tertiary)' : 'var(--text-primary)',
                textDecoration: chip.used ? 'line-through' : 'none',
              }}
            >
              {chip.form}
            </button>
          ))}
        </>
      )}
    </div>
  )

  const onArrow = (dir: 'up' | 'down') => {
    const target = dir === 'up' ? order[index - 1] : order[index + 1]
    if (target) goTo(target)
  }
  const leaveSheet = leaving && view.attempt && (
    <PlayerSheet
      testId="player-leave-sheet"
      title={t.leaveTitle}
      primary={{ label: t.leaveStay, onClick: stay, testId: 'player-leave-stay' }}
      secondary={{ label: t.leaveGo, onClick: leaveTo, testId: 'player-leave-go' }}
      dismiss={stay}
    >
      <p style={{ margin: 0 }}>{isViewer ? ts.viewerLeaveBody : t.leaveBody(view.attempt.answered, view.attempt.remaining)}</p>
    </PlayerSheet>
  )

  if (desktop) {
    return (
      <>
        <FillColumn
          testId="player-filling"
          // A click anywhere in the body never takes focus from the field; only the field itself does.
          onMouseDown={(e) => { if (!(e.target instanceof HTMLInputElement)) e.preventDefault() }}
        >
          <div className={CONTROL_SURFACE} onContextMenu={suppressTouchContextMenu} style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, fontFamily: 'var(--font-ui)' }}>
            {bankRow}
            <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
              <div
                ref={windowRef}
                data-testid="player-window"
                onScroll={onWindowScroll}
                style={{
                  position: 'absolute', inset: 0, overflowY: 'auto', overscrollBehavior: 'contain', boxSizing: 'border-box',
                  padding: '24px 32px 160px', borderRadius: 16, border: '1px solid var(--border)', background: 'var(--bg-surface)',
                }}
              >
                <PlayerText
                  segments={view.segments}
                  gaps={gaps}
                  mode="filling"
                  activeId={activeId}
                  typed={value}
                  onTapGap={(id) => { goTo(id); field.current?.focus() }}
                  fontSize={19}
                  lineHeight={38}
                  activeSize={{ minWidth: 64, height: 30 }}
                />
              </div>
              {pill && (
                <button
                  type="button"
                  data-testid="player-to-gap"
                  onClick={() => { setPill(false); scrollToActive() }}
                  style={{
                    position: 'absolute', left: '50%', bottom: 10, transform: 'translateX(-50%)', height: 36, padding: '0 14px',
                    display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)',
                    background: 'var(--bg-surface)', boxShadow: 'var(--shadow-float)', cursor: 'pointer',
                    color: 'var(--accent)', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-ui)',
                  }}
                >
                  <ArrowDown size={16} aria-hidden />{t.toGap}
                </button>
              )}
            </div>
            {/* Reserved even when empty, so the field below never moves (it grows upward, into the card). */}
            <div data-testid="player-slot" role="status" style={{ flexShrink: 0, minHeight: 40, boxSizing: 'border-box', display: 'flex', alignItems: 'center' }}>
              {slot}
            </div>
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <AnswerField
                ref={field}
                value={value}
                onChange={onType}
                onSubmit={submit}
                placeholder={t.placeholder}
                tone={tone}
                ariaLabel={t.answerLabel}
                submitLabel={t.check}
                nameKey={activeId ?? 'none'}
                keepFocus={keepFocus}
                variant={physicalKeyboard ? 'physical' : 'soft'}
                onArrow={onArrow}
                testId="player-answer"
              />
              {arrow('prev')}
              {arrow('next')}
            </div>
            <p data-testid="player-hint" style={{ flexShrink: 0, margin: 0, fontSize: 12, lineHeight: 1.4, color: 'var(--text-tertiary)' }}>{t.deskHint}</p>
          </div>
        </FillColumn>
        {leaveSheet}
      </>
    )
  }

  return (
    <div data-testid="player-filling" className={CONTROL_SURFACE} onContextMenu={suppressTouchContextMenu} style={frame}>
      {strip(
        <span data-testid="player-meta" style={{ flexShrink: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>
          {isViewer ? ts.stripLeft(remaining) : t.stripMeta(view.level, remaining)}
        </span>,
      )}
      <div style={column}>
        {bankRow}
        <div style={{ position: 'relative', flex: 1, minHeight: 0, margin: bankRow ? '0 16px' : '8px 16px 0' }}>
          <div
            ref={windowRef}
            data-testid="player-window"
            onScroll={onWindowScroll}
            // A tap anywhere in the text never takes the field's focus (the keyboard stays up).
            onPointerDown={(e) => e.preventDefault()}
            onMouseDown={(e) => e.preventDefault()}
            style={{
              position: 'absolute', inset: 0, overflowY: 'auto', overscrollBehavior: 'contain', boxSizing: 'border-box',
              padding: '4px 14px 140px', borderRadius: 16, border: '1px solid var(--border)', background: 'var(--bg-surface)',
            }}
          >
            <PlayerText
              segments={view.segments}
              gaps={gaps}
              mode="filling"
              activeId={activeId}
              typed={value}
              onTapGap={(id) => goTo(id)}
              fontSize={17}
              lineHeight={34}
            />
          </div>
          {pill && (
            <button
              type="button"
              data-testid="player-to-gap"
              onPointerDown={(e) => e.preventDefault()}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setPill(false); scrollToActive() }}
              style={{
                position: 'absolute', left: '50%', bottom: 10, transform: 'translateX(-50%)', height: 36, padding: '0 14px',
                display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)',
                background: 'var(--bg-surface)', boxShadow: 'var(--shadow-float)', cursor: 'pointer',
                color: 'var(--accent)', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-ui)',
              }}
            >
              <ArrowDown size={16} aria-hidden />{t.toGap}
            </button>
          )}
        </div>
        {/* The slot keeps its 48 px whether empty or full: the field never moves. */}
        <div data-testid="player-slot" role="status" style={{ flexShrink: 0, height: 48, boxSizing: 'border-box', display: 'flex', alignItems: 'center', padding: '0 16px', overflow: 'hidden' }}>
          {slot}
        </div>
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px 6px' }}>
          <AnswerField
            ref={field}
            value={value}
            onChange={onType}
            onSubmit={submit}
            placeholder={t.placeholder}
            tone={tone}
            ariaLabel={t.answerLabel}
            submitLabel={t.check}
            nameKey={activeId ?? 'none'}
            keepFocus={keepFocus}
            testId="player-answer"
          />
          {arrow('prev')}
          {arrow('next')}
        </div>
      </div>
      {leaveSheet}
    </div>
  )
}

/** The shared page's reading screen, drawn with the player's start handler. */
function ViewerReading({ render, onStart }: { render: ViewerMode['reading']; onStart: () => void }) {
  return <>{render(onStart)}</>
}

function Completion({ result, title, onAgain, onView, pendingAgain, desktop = false, onDone, onSave }: {
  result: PracticeCompletion
  title: string
  onAgain: () => void
  onView: () => void
  pendingAgain: boolean
  /** The centred card's content (§19.9): no scroller of its own, «Готово» in the footer row. */
  desktop?: boolean
  onDone?: () => void
  /**
   * A shared link's unsaved run (§17.2): no level to report, so the level card says the run
   * is not kept; the row is «Пройти ещё раз» and a ghost «Готово», and the primary is
   * «Сохранить в мои тексты».
   */
  onSave?: () => void
}) {
  const { tr } = useLocale()
  const t = tr.textPlayer
  const ts = tr.textShare
  const rose = result.level_after > result.level_before
  const atMax = !rose && result.level_after >= result.max_level
  const bankCounted = result.level_before === 2
  const stat = (name: string, note: string, n: number, testId: string, last = false) => (
    <div data-testid={testId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: last ? 'none' : '1px solid var(--border)' }}>
      <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{name}</span>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{note}</span>
      </span>
      <span style={{ fontFamily: 'var(--font-content)', fontSize: 26, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{n}</span>
    </div>
  )
  const condition = (met: boolean, text: string) => (
    <li style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--text-primary)' }}>
      {met ? <Check size={18} aria-hidden style={{ color: 'var(--accent)' }} /> : <X size={18} aria-hidden style={{ color: 'var(--text-tertiary)' }} />}
      {text}
    </li>
  )
  const conditions: React.ReactNode[] = []
  if (rose) {
    if (bankCounted) conditions.push(condition(true, t.condBankNotOpened))
    conditions.push(condition(true, t.condNoReveal))
  } else if (!atMax) {
    if (bankCounted) conditions.push(condition(!result.bank_opened, result.bank_opened ? t.condBankOpened : t.condBankOk))
    conditions.push(condition(result.revealed_count === 0, result.revealed_count === 0 ? t.condRevealOk : t.condRevealCount(result.revealed_count)))
  }
  const explanation = rose ? (result.level_after === 2 ? t.rose1 : t.rose2) : atMax ? t.maxText : t.stayedIntro(result.level_after + 1)

  const content = (
      <div style={desktop
        ? { display: 'flex', flexDirection: 'column', gap: 16 }
        : { maxWidth: 560, margin: '0 auto', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>{t.doneTitle}</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-secondary)' }}>{t.doneSub(title, result.total)}</p>
        </div>
        <section style={{ borderRadius: 16, border: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
          {stat(t.statFirst, t.statFirstNote, result.first_time_count, 'stat-first')}
          {stat(t.statHinted, t.statHintedNote, result.hinted_count, 'stat-hinted')}
          {stat(t.statRevealed, t.statRevealedNote, result.revealed_count, 'stat-revealed', true)}
        </section>
        {onSave ? (
          <section data-testid="completion-level" style={{ borderRadius: 16, border: '1px solid var(--border)', background: 'var(--bg-surface)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Eyebrow>{t.levelEyebrowDone}</Eyebrow>
            <span data-testid="completion-not-saved" style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{ts.notSaved}</span>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)' }}>{ts.notSavedBody}</p>
          </section>
        ) : (
        <section data-testid="completion-level" style={{ borderRadius: 16, border: '1px solid var(--border)', background: 'var(--bg-surface)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Eyebrow>{t.levelEyebrowDone}</Eyebrow>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span data-testid="completion-level-figure" style={{ fontFamily: 'var(--font-content)', fontSize: 32, fontWeight: 700, color: 'var(--text-primary)' }}>
              {rose ? `${result.level_before} → ${result.level_after}` : result.level_after}
            </span>
            <span data-testid="completion-level-tag" style={{ fontSize: 13, fontWeight: 600, color: rose ? 'var(--accent)' : 'var(--text-secondary)' }}>
              {rose ? t.newLevel : atMax ? t.maxLevel : t.unchanged}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)' }}>{explanation}</p>
          {conditions.length > 0 && <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>{conditions}</ul>}
        </section>
        )}
        {/* The note is about the learner's cards: a reader by link has none in play. */}
        {!onSave && <p data-testid="completion-note" style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: 'var(--text-tertiary)' }}>{t.practiceNote}</p>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SecondaryPill onClick={onAgain} disabled={pendingAgain} data-testid="completion-again">{t.again}</SecondaryPill>
          {onSave
            ? <GhostButton onClick={onView} data-testid="completion-done">{t.done}</GhostButton>
            : <GhostButton onClick={onView} data-testid="completion-view">{t.viewText}</GhostButton>}
          {desktop && onSave && (
            <>
              <span style={{ flex: 1 }} />
              <PrimaryButton height={44} onClick={onSave} data-testid="player-save" autoFocus>{ts.saveCta}</PrimaryButton>
            </>
          )}
          {desktop && !onSave && (
            <>
              <span style={{ flex: 1 }} />
              {/* Focused, so Enter closes the summary from the keyboard (§19.9). */}
              <PrimaryButton height={44} onClick={onDone} data-testid="player-done" autoFocus>{t.done}</PrimaryButton>
            </>
          )}
        </div>
      </div>
  )
  return desktop ? content : <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{content}</div>
}
